import { Button, GenericDialog, InputGroup } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useEffect, useState } from 'react'
import { useUpdateCbamSimulation } from '@/features/cbam-simulations-v2/hooks/cbamSimulations'
import { useSession } from '@/shared/hooks/useSession'
import toast from 'react-hot-toast'
import { trackCbamSimulationSignal } from '@/features/cbam-simulations-v2/analytics/cbamSimulationClarity'

export default function EditCbamSimulationDialog({
  isOpen,
  close,
  simulationName,
  simulationId
}: {
  isOpen: boolean
  close: () => void
  simulationName: string
  simulationId: string
}) {
  const { t } = useI18n()
  const organizationId = useSession().session.organization.id
  const { mutateAsync: updateSimulation, isPending: isUpdatingSimulation } =
    useUpdateCbamSimulation(organizationId, simulationId)

  const [name, setName] = useState(simulationName)

  useEffect(() => {
    setName(simulationName)
  }, [simulationName])

  const onClose = useCallback(() => {
    setName(simulationName)
    close()
  }, [close, simulationName])

  const onSubmit = useCallback(async () => {
    trackCbamSimulationSignal('simulations:edit:submitted')
    try {
      await updateSimulation({ name })
      trackCbamSimulationSignal('simulations:edit:succeeded')
      toast.success(
        t('cbamSimulations:cbamSimulationsPage.dialogs.editSimulation.notifications.success')
      )
      onClose()
    } catch (error) {
      console.error(error)
      trackCbamSimulationSignal('simulations:edit:failed')
      toast.error(
        t('cbamSimulations:cbamSimulationsPage.dialogs.editSimulation.notifications.error')
      )
    }
  }, [updateSimulation, name, t, onClose])

  const isSaveDisabled = isUpdatingSimulation || !name.trim()

  return (
    <GenericDialog
      isOpen={isOpen}
      close={onClose}
      title={t('cbamSimulations:cbamSimulationsPage.dialogs.editSimulation.title')}
      size="md"
    >
      <div className="space-y-4">
        <form
          onSubmit={evt => {
            evt.preventDefault()
            onSubmit()
          }}
        >
          <div className="space-y-4">
            <InputGroup
              label={t('cbamSimulations:cbamSimulationsPage.dialogs.editSimulation.name.label')}
              placeholder={t(
                'cbamSimulations:cbamSimulationsPage.dialogs.editSimulation.name.placeholder'
              )}
              value={name}
              onChange={evt => setName(evt.target.value)}
            />
          </div>
        </form>
        <footer className="flex justify-end gap-2">
          <Button type="button" variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isSaveDisabled}
            isLoading={isUpdatingSimulation}
            onClick={onSubmit}
          >
            {t('common:actions.save')}
          </Button>
        </footer>
      </div>
    </GenericDialog>
  )
}
